"use client"

import { useCallback, useEffect } from "react"
import { driver } from "driver.js"

const TOUR_SEEN_KEY = "dashboard:tour:seen"

interface DashboardTourProps {
    autoStart?: boolean
}

export function DashboardTour({ autoStart = false }: DashboardTourProps) {
    const startTour = useCallback(() => {
        const allSteps = [
            {
                element: "#dashboard-tour-header",
                popover: {
                    title: "Your Dashboard",
                    description: "This is your home base. Get a real-time snapshot of your performance, upcoming test dates, course progress, and daily activity — all in one place.",
                    side: "bottom" as const,
                    align: "start" as const,
                },
            },
            {
                element: "#dashboard-tour-access-mode",
                popover: {
                    title: "Access Mode",
                    description: "Switch between <b>Easy</b> and <b>Expert</b> mode. Easy mode simplifies the sidebar and surfaces the most essential tools. Expert mode unlocks the full feature set.",
                    side: "bottom" as const,
                    align: "start" as const,
                },
            },
            {
                element: "#dashboard-tour-discord",
                popover: {
                    title: "Community — Discord",
                    description: "Join the Discord server to connect with other students, ask questions, share strategies, and get real-time support from peers and mentors.",
                    side: "bottom" as const,
                    align: "start" as const,
                },
            },
            {
                element: "#dashboard-tour-messages",
                popover: {
                    title: "Messages",
                    description: "View your message inbox. New messages from mentors or connections appear here with a badge count. Click to open the full conversation thread.",
                    side: "bottom" as const,
                    align: "end" as const,
                },
            },
            {
                element: "#dashboard-tour-score",
                popover: {
                    title: "Projected Score",
                    description: "Your estimated SAT score based on recent performance. The breakdown shows separate Reading & Writing and Math projections so you can identify where to focus.",
                    side: "top" as const,
                    align: "start" as const,
                },
            },
            {
                element: "#dashboard-tour-activity",
                popover: {
                    title: "Activity",
                    description: "Your past 7 days of practice against your daily goal. Each day shows how many questions you got right and whether you hit the goal. Select any day to see a detailed breakdown.",
                    side: "top" as const,
                    align: "start" as const,
                },
            },
            {
                element: "#dashboard-tour-countdown",
                popover: {
                    title: "Test Countdown",
                    description: "Tracks days remaining until your next scheduled SAT. Use this to pace your preparation and ensure you cover all skills well before test day.",
                    side: "left" as const,
                    align: "start" as const,
                },
            },
            {
                element: "#dashboard-tour-lms",
                popover: {
                    title: "Your Courses",
                    description: "Browse and resume enrolled courses. Each card shows your progress through structured lessons designed to build skills systematically before practice.",
                    side: "left" as const,
                    align: "start" as const,
                },
            },
        ]

        // Only include steps whose target element actually exists in the DOM
        const steps = allSteps.filter(
            (step) => !step.element || !!document.querySelector(step.element)
        )

        const driverObj = driver({
            showProgress: true,
            showButtons: ["next", "previous", "close"],
            progressText: "{{current}} of {{total}}",
            nextBtnText: "Next",
            prevBtnText: "Back",
            doneBtnText: "Done",
            popoverClass: "satbuddy-tour-popover",
            onPopoverRender: (popover, { driver: d }) => {
                // Inject "Skip All" button next to the progress text
                const footer = popover.footer
                const progressText = footer.querySelector(".driver-popover-progress-text")
                if (progressText && !footer.querySelector(".satbuddy-skip-btn")) {
                    const skipBtn = document.createElement("button")
                    skipBtn.textContent = "Skip all"
                    skipBtn.className = "satbuddy-skip-btn"
                    skipBtn.addEventListener("click", () => d.destroy())
                    footer.insertBefore(skipBtn, progressText.nextSibling)
                }
                // Copy the current theme's resolved CSS variable values directly onto
                // the popover element so all var(--x) declarations resolve correctly
                // regardless of which theme class is active on <html>.
                const computed = getComputedStyle(document.documentElement)
                const vars = [
                    "--background", "--foreground",
                    "--card", "--card-foreground",
                    "--primary", "--primary-foreground",
                    "--muted", "--muted-foreground",
                    "--accent", "--accent-foreground",
                    "--border", "--input", "--ring", "--radius",
                ]
                vars.forEach((v) => {
                    const value = computed.getPropertyValue(v).trim()
                    if (value) popover.wrapper.style.setProperty(v, value)
                })
                // Apply the app font directly
                const fontFamily = computed.getPropertyValue("--font-sans").trim()
                    || computed.fontFamily
                popover.wrapper.style.setProperty("font-family", fontFamily || "'DM Sans', ui-sans-serif, system-ui, sans-serif")
            },
            steps,
        })

        driverObj.drive()
        try { localStorage.setItem(TOUR_SEEN_KEY, "1") } catch { }
    }, [])

    // Auto-start once after mount when it's the user's first visit
    useEffect(() => {
        if (!autoStart) return
        // Small delay so all tour-target elements are painted before driver queries them
        const id = setTimeout(startTour, 600)
        return () => clearTimeout(id)
    }, [autoStart, startTour])

    return null
}
